import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import usePlaceDetails from './ItineraryPage/usePlaceDetails';
import useItinerary from './ItineraryPage/useItinerary';
import MapSection from './ItineraryPage/MapSection';
import ItinerarySection from './ItineraryPage/ItinerarySection';
import useApi from '../Api';
import '../styles/pages/ItineraryPage/ItineraryPageLayout.css';
import '../styles/pages/ItineraryPage/MapStyles.css';
import '../styles/pages/ItineraryPage/PlaceInfoBlock.css';
import { useAuth } from '../AuthContext';
import { usePlaceContext } from '../components/itinerary/PlaceContext';
import ItineraryHeader from '../components/itinerary/ItineraryHeader';
import { format } from 'date-fns';
import useReadingTracker from '../components/common/useReadingTracker';

const ItineraryViewPage = () => {
  const { origin_id: originParam, itinerary_id } = useParams();
  const origin_id = parseInt(originParam, 10);
  const placeDetails = usePlaceDetails(origin_id);
  const { user, token } = useAuth();
  const { itineraries, selectedItinerary, setSelectedItinerary } = useItinerary(placeDetails);
  const { state, fetchItineraries, fetchItineraryDetails } = usePlaceContext();
  const [isSectionOpen, setIsSectionOpen] = useState({ place: true, itinerary: true });
  const [showMap, setShowMap] = useState(window.innerWidth > 1024);
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth <= 1024);
  const [error, setError] = useState(null);
  const lastFetchedItineraryId = useRef(null);
  const hasFetchedRef = useRef(false);
  const apiClient = useApi();
  const viewCallLockRef = useRef(false);
  const recordedItineraryIdRef = useRef(null);
  const itineraryRef = useRef(null);
  const mapSectionRef = useRef(null);

  useReadingTracker(itinerary_id, 'itinerary', user ? user.uid : null);

  useEffect(() => {
    const handleResize = () => {
      const smallScreen = window.innerWidth <= 1024;
      setIsSmallScreen(smallScreen);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const loadItineraries = async () => {
      if (!placeDetails) return;
      if (!itinerary_id) {
        if (selectedItinerary) setSelectedItinerary(null);
        return;
      }
      if (!state.itinerariesLoaded) await fetchItineraries(origin_id);
    };
    loadItineraries();
  }, [placeDetails, itinerary_id, origin_id, fetchItineraries, selectedItinerary, setSelectedItinerary, state.itinerariesLoaded, user]);

  useEffect(() => {
    hasFetchedRef.current = false;
  }, [itinerary_id]);

  useEffect(() => {
    const loadSelectedItinerary = async () => {
      if (!placeDetails) return;
      if (!itinerary_id) {
        setSelectedItinerary(null);
        return;
      }
      if (hasFetchedRef.current) return;
      hasFetchedRef.current = true;
      let itinerary = state.itineraries.find(it => it.id === itinerary_id);
      if (!itinerary) {
        itinerary = { id: itinerary_id, name: "Public Itinerary", columns: [] };
      }
      try {
        const { itinerary: fullItinerary } = await fetchItineraryDetails(itinerary, { readOnly: true });
        setSelectedItinerary(fullItinerary);
        lastFetchedItineraryId.current = fullItinerary.id;
      } catch (err) {
        setError(err.message);
      }
    };
    loadSelectedItinerary();
  }, [placeDetails, itinerary_id, fetchItineraryDetails, setSelectedItinerary, state.itineraries]);

  useEffect(() => {
    if (itinerary_id && token && selectedItinerary && recordedItineraryIdRef.current !== itinerary_id && !viewCallLockRef.current) {
      viewCallLockRef.current = true;
      apiClient
        .post(`/itineraries/${itinerary_id}/record-view`, null, {
          headers: { Authorization: `Bearer ${token}` }
        })
        .catch(err => {
          console.error("Failed to record view:", err);
        })
        .finally(() => {
          recordedItineraryIdRef.current = itinerary_id;
          viewCallLockRef.current = false;
        });
    }
  }, [itinerary_id, token, selectedItinerary, apiClient]);

  useEffect(() => {
    if (isSmallScreen) {
      if (showMap) {
        mapSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
      } else {
        itineraryRef.current?.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [showMap, isSmallScreen]);

  const toggleMap = () => setShowMap(prev => !prev);

  const itineraryTags = selectedItinerary?.tags?.map(t => t.name) || [];
  const formattedDate = selectedItinerary?.updated_datetime_utc ? format(new Date(selectedItinerary.updated_datetime_utc), 'do MMMM yyyy') : null;
  const itineraryTitle = selectedItinerary?.name || "View Itinerary";

  return (
    <>
      <Helmet>
        <title>{`${itineraryTitle} - WeiFinders`}</title>
        <meta name="description" content="View travel itinerary on WeiFinders." />
        <meta property="og:title" content={`${itineraryTitle} - WeiFinders`} />
        <meta property="og:description" content="View travel itinerary on WeiFinders." />
        <meta property="og:site_name" content="WeiFinders" />
        <meta property="og:type" content="website" />
        <link rel="canonical" href={`https://weifinders.com/itinerary/${originParam}/${itinerary_id}`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@WeiFinders" />
        <meta name="twitter:title" content={`${itineraryTitle} - WeiFinders`} />
        <meta name="twitter:description" content="View travel itinerary on WeiFinders." />
        {selectedItinerary && selectedItinerary.thumbnail_url && (
          <>
            <meta property="og:image" content={selectedItinerary.thumbnail_url} />
            <meta name="twitter:image" content={selectedItinerary.thumbnail_url} />
          </>
        )}
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "${itineraryTitle} - WeiFinders",
            "url": "https://weifinders.com/itinerary/${originParam}/${itinerary_id}",
            "description": "View travel itinerary on WeiFinders."
          }
        `}</script>
      </Helmet>
      {(!placeDetails) ? (
        <div>Loading...</div>
      ) : error ? (
        <div style={{ padding: "1rem", textAlign: "center" }}>{error}</div>
      ) : (itinerary_id && !state.itinerariesLoaded) ? (
        <div>Loading itineraries...</div>
      ) : (
        <div className={`itinerary-page ${isSmallScreen && showMap ? 'fullscreen-map-active' : ''}`}>
          <main role="main">
            <div className="content-wrapper">
              <div className="info-block" ref={itineraryRef}>
                <ItineraryHeader
                  onCreateNew={() => {}}
                  onRenameItinerary={() => {}}
                  onDeleteItinerary={() => {}}
                  onUpdatePrivacy={() => {}}
                  isCreatingItinerary={false}
                  isRenamingItinerary={false}
                  isDeletingItinerary={false}
                  isUpdatingPrivacy={false}
                  activeSection="itinerary"
                  setActiveSection={() => {}}
                  readOnly={true}
                  additionalInfoData={{
                    destinationName: placeDetails.name,
                    destinationLink: `/destination/${origin_id}`,
                    tags: itineraryTags,
                    authorName: selectedItinerary?.author_name,
                    updatedDate: formattedDate,
                    hideThumbnail: true
                  }}
                />
                <ItinerarySection
                  itineraries={itineraries}
                  selectedItinerary={selectedItinerary}
                  handleCreateNewItinerary={() => {}}
                  handleRenameItinerary={() => {}}
                  handleDeleteItinerary={() => {}}
                  setSelectedItinerary={setSelectedItinerary}
                  isSectionOpen={isSectionOpen}
                  setIsSectionOpen={setIsSectionOpen}
                  handleUpdatePrivacy={() => {}}
                  isCreatingItinerary={false}
                  isRenamingItinerary={false}
                  isDeletingItinerary={false}
                  isUpdatingPrivacy={false}
                  toggleMap={toggleMap}
                  isSmallScreen={isSmallScreen}
                  showMap={showMap}
                  readOnly={true}
                />
              </div>
              <MapSection
                ref={mapSectionRef}
                placeDetails={placeDetails}
                toggleMap={toggleMap}
                isSmallScreen={isSmallScreen}
                showMap={showMap}
                readOnly={true}
                activeSection="itinerary"
              />
            </div>
          </main>
        </div>
      )}
    </>
  );
};

export default ItineraryViewPage;
